import { observer } from 'mobx-react';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CollapseHeight } from 'constants/collapse-height';
import { FilterModel, FilterOptionModel } from 'components/common/search-form/fields/filter-types';

import { UitkExpandoPeek } from 'uitk-react-expando';
import { UitkRadio, UitkRadioGroup } from 'uitk-react-switch';

import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form/index';

const RadioButtonTracked = ClickTracker(UitkRadio);

export type RadioGroupProps = LocalizedComponentProps & SearchFormFieldProps;

export interface RadioGroupState {
  viewExpanded: boolean;
}

@withLocalization
@FormFieldBinding
@observer
export class RadioGroup extends React.Component<RadioGroupProps, RadioGroupState> {
  constructor(props) {
    super(props);

    this.state = { viewExpanded: false };

    this.handleChange = this.handleChange.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
  }

  private handleToggleClick() {
    this.setState(prevState => ({ viewExpanded: !prevState.viewExpanded }));
  }

  private handleChange(evt) {
    const target = evt.target;
    const { value } = target;

    const { model, modelKey, sortAndFilterOptions, updateModel } = this.props;

    const radioGroup: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const radioGroupName: string = radioGroup && radioGroup.name;
    const modelValue = model && model[radioGroupName];

    if (!(typeof updateModel === 'function') || typeof modelValue === 'undefined') {
      return;
    }

    updateModel({
      target: {
        name: radioGroupName,
        value
      }
    });
  }

  public render() {
    const { l10n, model, modelKey, sortAndFilterOptions } = this.props;
    const { viewExpanded } = this.state;

    const { formatText } = l10n;

    const radioGroup = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const radioGroupOptions = radioGroup && radioGroup.options;
    const hiddenThreshold = radioGroup && radioGroup.hiddenThreshold;
    const collapseHeight = hiddenThreshold && hiddenThreshold === 5 ? CollapseHeight.FIVE_HEIGHT : CollapseHeight.TEN_HEIGHT;
    const radioGroupName: string = radioGroup && radioGroup.name;

    if (!radioGroupOptions || radioGroupOptions.length === 0) {
      return null;
    }

    return (
      <fieldset>
        <legend id={`${radioGroupName}-legend`} className="filter-title uitk-type-heading-500 uitk-type-bold">
          {radioGroup.label}
        </legend>
        {hiddenThreshold && radioGroupOptions.length > hiddenThreshold ? (
          <UitkExpandoPeek
            className="pwa-switch-group all-b-margin-six"
            collapseHeight={collapseHeight}
            collapseLabel={formatText('contentToggle.collapse.label')}
            expandLabel={formatText('contentToggle.expand.label')}
            handleClick={this.handleToggleClick}
            isVisible={viewExpanded}
          >
            <Radios handleChange={this.handleChange} model={model} modelKey={modelKey} sortAndFilterOptions={sortAndFilterOptions} />
          </UitkExpandoPeek>
        ) : (
          <div className="pwa-switch-group all-b-margin-six">
            <Radios handleChange={this.handleChange} model={model} modelKey={modelKey} sortAndFilterOptions={sortAndFilterOptions} />
          </div>
        )}
      </fieldset>
    );
  }
}

const Radios = observer(({ handleChange, model, modelKey, sortAndFilterOptions }) => {
  const radioGroup: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
  const radioGroupOptions: FilterOptionModel[] = radioGroup && radioGroup.options;
  const radioGroupName: string = radioGroup && radioGroup.name;

  const value = (model && model[radioGroupName]) || '';

  let hiddenField;
  if (value) {
    if (!radioGroupOptions.some(option => option.value === value)) {
      hiddenField = <input type="radio" hidden={true} name={radioGroupName} checked={true} value={value} />;
    }
  }

  return (
    <React.Fragment>
      <UitkRadioGroup group={radioGroupName} onChange={handleChange} selected={value}>
        {radioGroupOptions.map((option, idx) => (
          <RadioButtonTracked
            description={option.description}
            events="click"
            id={`radio-${radioGroupName}-${option.value}`}
            key={`${radioGroupName}-${idx}-${option.value}`}
            label={option.label}
            linkName={`${radioGroupName}.${option.value}`}
            name={option.value}
            rfrr={`${radioGroupName}.${option.value}`}
            value={option.value}
            data-stid={`radio-${radioGroupName}-${option.value}`}
            group={radioGroup.label}
          />
        ))}
      </UitkRadioGroup>
      {hiddenField}
    </React.Fragment>
  );
});
